<template>
  <div class="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
    <transition-group name="toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="pointer-events-auto min-w-[300px] rounded-lg border bg-white p-4 shadow-lg flex items-center gap-3 animate-in slide-in-from-right-full"
        :class="[
          toast.type === 'error' ? 'border-red-200 bg-red-50 text-red-900' : 
          toast.type === 'success' ? 'border-green-200 bg-green-50 text-green-900' : 
          'border-gray-200 bg-white text-gray-900'
        ]"
      >
        <div class="flex-1">
          <p class="text-sm font-medium">{{ toast.message }}</p>
          <p v-if="toast.description" class="text-xs opacity-80">{{ toast.description }}</p>
        </div>
        <button @click="remove(toast.id)" class="text-lg leading-none opacity-50 hover:opacity-100">&times;</button>
      </div>
    </transition-group>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';

export interface Toast {
  id: number;
  message: string;
  description?: string;
  type: 'success' | 'error' | 'info';
  duration?: number;
}

const toasts = ref<Toast[]>([]);
let nextId = 0;

export const useToast = () => {
  const toast = (message: string, options: Partial<Omit<Toast, 'id' | 'message'>> = {}) => {
    const id = nextId++;
    const newToast: Toast = {
      id,
      message,
      type: options.type || 'info',
      description: options.description,
      duration: options.duration || 5000,
    };
    
    toasts.value.push(newToast);
    
    if (newToast.duration !== Infinity) {
      setTimeout(() => {
        remove(id);
      }, newToast.duration);
    }
  };

  const remove = (id: number) => {
    const index = toasts.value.findIndex(t => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  };

  return { toast, remove };
};

export default defineComponent({
  name: 'Toaster',
  setup() {
    return {
      toasts,
      remove: (id: number) => {
        const index = toasts.value.findIndex(t => t.id === id);
        if (index !== -1) {
          toasts.value.splice(index, 1);
        }
      }
    };
  }
});
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(30px);
}
.toast-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
</style>
